import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../../../core/common/dataTable/index";
import { exam } from "../../../../core/data/json/exam";
import dayjs from "dayjs";
import { all_routes } from "../../../router/all_routes";
import { useUserPermissions } from "../../../UserPermissionsContext";
import Cookies from "js-cookie";
import Spin from "antd/es/spin";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { DateRange } from "react-date-range";
import CommonSelect from "../../../../core/common/commonSelect";

type Permission = { id: number; name: string };
type Admin = {
  id: number;
  username: string;
  total_calls: number;
  total_duration: string;
  total_answered_calls_by_agent: number;
  total_answered_calls_by_parent: number;
  total_missed_calls_by_agent: number;
  total_missed_calls_by_parent: number;
  last_call_date: string | null;
};

type Service_division = {
  id: number,
  name: string,
}

export type Option = {
  value: string;
  label: string;
};

const getStoredServiceDivision = (): Option | null => {
  const stored = localStorage.getItem('employeeCallSummarySelectedServiceDivision');
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

const EmployeeCallSummary = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const data = exam;
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Month is zero-based, so we add 1
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${month}-${day}-${year}`;
  const defaultValue = dayjs(formattedDate);
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [admin, setAdmin] = useState<Admin[]>([]);
  const [from, setFrom] = useState();
  const [to, setTo] = useState<number>();
  const [totalCount, setTotalCount] = useState();
  const [toastMessage, setToastMessage] = useState("");
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>("");
  const [showDateRange, setShowDateRange] = useState(false);
  const [showCalendar1, setShowCalendar1] = useState(false);
  const [serviceDivisionDropdown, setServiceDivisionDropdown] = useState<Service_division[]>([]);
  const [selectedServiceDivision, setSelectedServiceDivision] = useState<Option | null>(
    getStoredServiceDivision()
  );


  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject
    );
  };

  // Fix the typo: change "cconst" to "const"
  const [dateRange, setDateRange] = useState(() => {
    // Try to get stored date range from localStorage
    const storedDateRange = localStorage.getItem('dateRange');

    if (storedDateRange) {
      const parsedDateRange = JSON.parse(storedDateRange);
      return [
        {
          startDate: new Date(parsedDateRange[0].startDate),
          endDate: new Date(parsedDateRange[0].endDate),
          key: "selection",
        },
      ];
    }

    // Default date range if nothing is stored
    return [
      {
        startDate: new Date(new Date().setHours(0, 0, 0, 0)),
        endDate: new Date(new Date().setHours(0, 0, 0, 0)),
        key: "selection",
      },
    ];
  });

  // Update localStorage whenever dateRange changes
  useEffect(() => {
    localStorage.setItem('dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  const startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
  const endDate = format(dateRange[0].endDate, "yyyy-MM-dd");

  useEffect(() => {
    if (!checkPermission("agent_employee_call_summary", "Records")) {
      navigate(routes.adminDashboard);
    } else {
      fetchServiceDivisions();
      fetchData(
        currentPage,
        pageSize,
        searchText,
        startDate || undefined,
        endDate || undefined,
        selectedServiceDivision?.value
      );
    }
  }, [userPermissions]);

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  useEffect(() => {
    if (selectedServiceDivision) {
      // Stringify the object before storing
      localStorage.setItem('employeeCallSummarySelectedServiceDivision', JSON.stringify(selectedServiceDivision));
    }
    // else {
    //     localStorage.removeItem('dailedEmployeeSelectedServiceDivision');
    // }
  }, [selectedServiceDivision]);


  const handleFetchData = () => {
    if (startDate && endDate) {
      fetchData(
        currentPage,
        pageSize,
        searchText,
        startDate || undefined,
        endDate || undefined,
        selectedServiceDivision?.value
      ); // Call fetchData with date range
      setShowCalendar1(false); // Hide the date range picker after selecting dates
    }
  };

  useEffect(() => {
    fetchData(
      currentPage,
      pageSize,
      searchText,
      startDate || undefined,
      endDate || undefined,
      selectedServiceDivision?.value
    );
  }, [currentPage, pageSize, searchText])

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  const fetchServiceDivisions = async () => {
    try {
      const response = await fetch(
        `${webApi}/employees/teacher_service_division_dropdown_based_on_user_with_all/`,
        options
      );
      const data = await response.json();
      setServiceDivisionDropdown(data);
    } catch (error) {
      console.error("Failed to fetch agents", error);
    }
  }


  const fetchGroupsData = async (
    page: number,
    pageSize: number,
    searchText: string,
    startDate = "",
    endDate = "",
    serviceDivision: any,
  ) => {
    let apiUrl = `${webApi}/call/agent_wise_employee_call_analytics/?page=${page}&page_size=${pageSize}&search=${searchText}&start_date=${startDate}&end_date=${endDate}`
    if (serviceDivision) apiUrl += `&service_division_id=${serviceDivision}`
    const response = await fetch(
      apiUrl,
      options
    );
    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.code === "token_not_valid") {
        navigate("/login");
      }
      throw new Error(errorData.detail || "Failed to fetch user data");
    }
    return await response.json();
  };

  const fetchPermissionsData = async () => {
    const resposne = await fetch(
      `${webApi}/user/get_permissions_dropdown/`,
      options
    );
    return await resposne.json();
  };

  const fetchData = async (
    page: number,
    pageSize: number,
    searchText: string,
    startDate = "",
    endDate = "",
    serviceDivision: any,
  ) => {
    setIsLoading(true); // Start loading
    try {
      const [groupsData] = await Promise.all([
        fetchGroupsData(page, pageSize, searchText, startDate, endDate, serviceDivision),
        // fetchPermissionsData(),
      ]);
      const serviceDivisions = groupsData.results.service_divisions;
      setSelectedServiceDivision(
        serviceDivisions && serviceDivisions.length > 0
          ? {
            value: serviceDivisions[0].id.toString(),
            label: serviceDivisions[0].name,
          }
          : null
      );

      setTotalPages(groupsData.total_pages);
      setAdmin(groupsData.results.results);
      // setPermissions(permissionsData.results);

      setFrom(groupsData.from);
      setTo(groupsData.to);
      setTotalCount(groupsData.total_count);
      setToastMessage(groupsData.message || "submitted successfully!");
      setShowSuccessToast(true);
      setTotalCount(groupsData.total_count);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    setPageSize(pageSize);
    // fetchData(
    //   current,
    //   pageSize,
    //   searchText,
    //   startDate || undefined,
    //   endDate || undefined
    // ); // Fetch new data based on the updated pagination
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    // fetchData(
    //   currentPage,
    //   pageSize,
    //   value,
    //   startDate || undefined,
    //   endDate || undefined
    // );
  };

  const handleServiceDivisionChange = async (selected: Option | null) => {
    const serviceDivision = serviceDivisionDropdown.find(
      (c) => c.id === parseInt(selected?.value || "")
    );

    const newServiceDivisionValue = serviceDivision ? serviceDivision.id.toString() : null;

    // Update state
    setSelectedServiceDivision(
      serviceDivision
        ? {
          value: serviceDivision.id.toString(),

          label: serviceDivision.name,
        }
        : null
    );
    fetchData(
      currentPage,
      pageSize,
      searchText,
      startDate || undefined,
      endDate || undefined,
      newServiceDivisionValue
    );
    setCurrentPage(1);
  };

  const serviceDivisionOptions = serviceDivisionDropdown.map(serviceDivision => ({
    value: String(serviceDivision.id),
    label: serviceDivision.name
  }));


  const [loading, setLoading] = useState(false);

  const handleExportClick = async () => {
    let apiUrl = `${webApi}/exports/export_employee_call_analytics/?token=${jwtToken}&start_date=${startDate}&end_date=${endDate}`;
    if (selectedServiceDivision?.value) apiUrl += `&service_division_id=${selectedServiceDivision.value}`

    window.location.href = apiUrl

  };

  const columns = [
    {
      title: "Agent",
      dataIndex: "username",
      render: (
        text:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        record: { id: any }
      ) => (
        <Link to={`${routes.employeeAgentWiseCallLog}?id=${record.id}&startDate=${startDate}&endDate=${endDate}&serviceDivision=${selectedServiceDivision?.value}`}>
          {text}
        </Link>
      ),
    },
    {
      title: "Unique Lead Touch",
      dataIndex: "unique_lead_touch",
      render: (
        text:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        record: { id: any }
      ) => (
        <Link to={`${routes.employeeAgentWiseCallLog}?id=${record.id}&startDate=${startDate}&endDate=${endDate}&serviceDivision=${selectedServiceDivision?.value}`}>
          {text}
        </Link>
      ),
    },
    {
      title: "Total Calls",
      dataIndex: "total_calls",
      render: (
        text:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        record: { id: any }
      ) => (
        <Link to={`${routes.employeeAgentWiseCallLog}?id=${record.id}&startDate=${startDate}&endDate=${endDate}&serviceDivision=${selectedServiceDivision?.value}`}>
          {text}
        </Link>
      ),
    },
    {
      title: "Conversation Duration",
      dataIndex: "total_duration",
      render: (
        text:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        record: { id: any }
      ) => (
        <Link to={`${routes.employeeAgentWiseCallLog}?id=${record.id}&startDate=${startDate}&endDate=${endDate}&serviceDivision=${selectedServiceDivision?.value}`}>
          {text}
        </Link>
      ),
    },
    {
      title: "Total Answered Calls by Employee",
      dataIndex: "total_answered_calls_by_employee",
      render: (
        text:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        record: { id: any }
      ) => (
        <Link to={`${routes.employeeAgentWiseCallLog}?id=${record.id}&startDate=${startDate}&endDate=${endDate}&serviceDivision=${selectedServiceDivision?.value}`}>
          {text}
        </Link>
      ),
    },
    {
      title: "Total Answered Calls by Agent",
      dataIndex: "total_answered_calls_by_agent",
      render: (
        text:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        record: { id: any }
      ) => (
        <Link to={`${routes.employeeAgentWiseCallLog}?id=${record.id}&startDate=${startDate}&endDate=${endDate}&serviceDivision=${selectedServiceDivision?.value}`}>
          {text}
        </Link>
      ),
    },
    {
      title: "Total Missed Calls by Agent",
      dataIndex: "total_missed_calls_by_agent",
      render: (
        text:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        record: { id: any }
      ) => (
        <Link to={`${routes.employeeAgentWiseCallLog}?id=${record.id}&startDate=${startDate}&endDate=${endDate}&serviceDivision=${selectedServiceDivision?.value}`}>
          {text}
        </Link>
      ),
    },
    {
      title: "Total Missed Calls by Employee",
      dataIndex: "total_missed_calls_by_employee",
      render: (
        text:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        record: { id: any }
      ) => (
        <Link to={`${routes.employeeAgentWiseCallLog}?id=${record.id}&startDate=${startDate}&endDate=${endDate}&serviceDivision=${selectedServiceDivision?.value}`}>
          {text}
        </Link>
      ),
    },
    {
      title: "Last Call Date",
      dataIndex: "last_call_date",
      render: (
        text:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        record: { id: any }
      ) => (
        <Link to={`${routes.employeeAgentWiseCallLog}?id=${record.id}&startDate=${startDate}&endDate=${endDate}&serviceDivision=${selectedServiceDivision?.value}`}>
          {text}
        </Link>
      ),
    },

  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Employee Agents Summary</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Agents Summary
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {checkPermission("can_export_employee_call_analytics", "Exports") && (
                <button className="btn btn-primary me-2" onClick={handleExportClick} disabled={loading}>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    "Export"
                  )}
                </button>
              )}
            </div>
          </div>
          {/* /Page Header */}
          {/* Guardians List */}
          <div
            className="card "
            style={{
              width: "100%",
              boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
              borderRadius: "10px",
            }}
          >
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              {/* <h4 className="mb-3">Agents Summary List</h4> */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "8px",
                  width: "100%",
                  justifyContent: "flex-end"
                }}
              >
                {/* CommonSelect */}
                <div style={{
                  minWidth: "200px",
                  maxWidth: "250px",
                  width: "auto",
                  marginBottom: "13px",
                  zIndex: 9999,
                  position: "relative",
                  marginRight: "8px"
                }}>
                  <CommonSelect
                    className="select"
                    options={serviceDivisionOptions}
                    onChange={handleServiceDivisionChange}
                    value={selectedServiceDivision}
                    isRequired
                    styles={{
                      menu: (provided: any) => ({
                        ...provided,
                        zIndex: 9999,
                        position: "absolute"
                      }),
                      menuPortal: (provided: any) => ({
                        ...provided,
                        zIndex: 9999
                      }),
                      container: (provided: any) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>

                {/* Date Range Picker */}
                <div style={{
                  position: "relative",
                  minWidth: "200px",
                  maxWidth: "250px",
                  width: "auto",
                  marginRight: "8px"
                }}>
                  <div
                    onClick={() => setShowCalendar1(!showCalendar1)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 15px",
                      background: "white",
                      borderRadius: "20px",
                      boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: "#34303D",
                      cursor: "pointer",
                      border: "1px solid #ddd",
                      minWidth: "200px",
                      justifyContent: "center",
                      marginBottom: "15px",
                      boxSizing: "border-box"
                    }}
                  >
                    {format(dateRange[0].startDate, "dd MMM yy")} –{" "}
                    {format(dateRange[0].endDate, "dd MMM yy")}
                    <span style={{ marginLeft: "10px" }}>▼</span>
                  </div>

                  {/* Calendar Popup */}
                  {showCalendar1 && (
                    <div
                      style={{
                        position: "absolute",
                        zIndex: 2000,
                        background: "white",
                        borderRadius: "10px",
                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                        padding: "5px",
                        top: "100%",
                        right: "0",
                        marginTop: "5px"
                      }}
                    >
                      <DateRange
                        ranges={dateRange}
                        onChange={(item) =>
                          setDateRange([
                            {
                              startDate: item.selection.startDate || new Date(),
                              endDate: item.selection.endDate || new Date(),
                              key: "selection",
                            },
                          ])
                        }
                        moveRangeOnFirstSelection={false}
                        rangeColors={["blue"]}
                      />
                    </div>
                  )}
                </div>

                {/* Search Button */}
                <div style={{
                  marginBottom: "7px"
                }}>
                  <button
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "#28a745",
                      borderColor: "#28a745",
                      color: "#fff",
                      height: "38px",
                      padding: "6px 20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "7px",
                      borderRadius: "19px",
                      minWidth: "120px",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      border: "none",
                      fontSize: "14px",
                      fontWeight: "bold"
                    }}
                    onClick={handleFetchData}
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              <div
                className="serch-global text-right"
                style={{ marginRight: "15px" }}
              >
                <input
                  type="search"
                  className="form-control form-control-sm mb-3 w-auto float-end"
                  value={searchText}
                  placeholder="Search"
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-controls="DataTables_Table_0"
                ></input>
              </div>
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    height: "100vh",
                    paddingTop: "20px",
                  }}
                >
                  <Spin /> {/* Display loader while loading */}
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={admin}
                  Selection={true}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    locale: { items_per_page: "" },
                    nextIcon: <span>Next</span>,
                    prevIcon: <span>Prev</span>,
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    total: totalCount,
                    showTotal: (total: any, range: any[]) =>
                      `${range[0]}-${range[1]} of ${total}`,
                    pageSizeOptions: ["15", "25", "50", "100"],
                  }}
                  onChange={handleTableChange} // Move this outside of pagination
                  onSearch={handleSearch}
                />
              )}
            </div>
          </div>
          {/* /Guardians List */}
        </div>
      </div>
      <>
        {/* Delete Modal */}
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form>
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    You want to delete all the marked items, this cant be undone
                    once you delete.
                  </p>
                  <div className="d-flex justify-content-center">
                    <Link
                      to="#"
                      className="btn btn-light me-3"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      data-bs-dismiss="modal"
                      className="btn btn-danger"
                    >
                      Yes, Delete
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Delete Modal */}
      </>
    </div>
  );
};

export default EmployeeCallSummary;
